<script setup lang="ts">
    import VueHcaptcha from "@hcaptcha/vue3-hcaptcha";
    import type { APIError, OrderPayload } from "common";
    import * as Sentry from "@sentry/vue";
    import { useOrderStore } from "~/composables/store/order";

    const orderStore = useOrderStore();
    const { t } = useI18n();

    const sendingOrder = ref(false);
    const submitError = ref<string>();

    const config = useRuntimeConfig();

    const hcaptchaComponent = ref<VueHcaptcha>();
    const verifiedCaptcha = ref(false);
    const hcaptchaToken = ref<string>();

    const onHcaptchaVerify = (token: string) => {
        hcaptchaToken.value = token;
        verifiedCaptcha.value = true;
    };

    const submit = async () => {
        orderStore.validation.r$.$touch();
        if (!orderStore.validation.isValid) {
            submitError.value = t("checkout.errors.validation");
            return;
        }

        sendingOrder.value = true;
        submitError.value = "";
        let token = hcaptchaToken.value;

        try {
            if (
                orderStore.options.toggleableFields.hcaptcha?.enabled &&
                !verifiedCaptcha.value
            ) {
                if (!hcaptchaComponent.value) {
                    throw new Error("Hcaptcha component is not available");
                }
                const { response } =
                    await hcaptchaComponent.value.executeAsync();
                token = response;
                verifiedCaptcha.value = true;
            }
        } catch {
            // Could not get a hcaptcha token, do not proceed and let hcaptcha component show an error
            return;
        }

        try {
            const payload = {
                ...orderStore.orderPayload,
                hcaptchaResponseToken: token,
            } as OrderPayload;

            const data = await $fetch<{
                order: {
                    id: number;
                    accessKey: string;
                };
                payment: {
                    provider: string;
                    redirectUrl: string;
                };
            }>(config.public.api_url + "/api/v2/order", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                retryStatusCodes: [502, 504],
                body: payload,
                onRequestError({ error }) {
                    submitError.value = t("checkout.errors.unexpected");
                    Sentry.captureException(error);
                },
                onResponseError({ request, response }) {
                    try {
                        const errorData: APIError = response._data;
                        hcaptchaComponent.value?.reset();

                        if (response.status === 503) {
                            submitError.value = t("checkout.errors.closed");
                        } else if (response.status === 400) {
                            if (
                                [-1500, -1501].includes(
                                    errorData.errorCode || 0,
                                )
                            ) {
                                submitError.value = t(
                                    "checkout.errors.hcaptcha",
                                );
                            } else if (errorData.errorCode === -400) {
                                submitError.value = t(
                                    "checkout.errors.noStock",
                                );
                                orderStore.helpers.refreshFulfillment();
                            } else if (errorData.errorCode === -401) {
                                submitError.value = t("checkout.errors.noTeam");
                                orderStore.helpers.refreshFulfillment();
                            }
                        }

                        if (submitError.value) {
                            // Already found an error cause
                            return;
                        }

                        submitError.value = t("checkout.errors.unexpected");
                        Sentry.captureException(
                            new Error(
                                `Error during submission of order: ${errorData.errorMsg} (${errorData.errorCode})`,
                                {
                                    cause: {
                                        request,
                                        response,
                                    },
                                },
                            ),
                        );
                    } catch (e) {
                        Sentry.captureException(e);
                        submitError.value = t("checkout.errors.unexpected");
                    }
                },
            });

            if (data.payment.redirectUrl) {
                await navigateTo(data.payment.redirectUrl, { external: true });
            } else {
                await navigateTo(
                    `/order/${data.order.id}?key=${data.order.accessKey}`,
                );
            }
        } catch {
            // Ignore, errors ignored in onResponseError and onRequestError
            return;
        } finally {
            sendingOrder.value = false;
        }
    };
</script>
<template>
    <VForm>
        <checkout-customer-info />
        <checkout-location />
        <template v-if="orderStore.meta.fulfillmentComplete">
            <template v-if="orderStore.meta.timeslotAvailabilityError === 0">
                <VAlert type="warning" class="mb-4">
                    {{ $t("checkout.location.allTimeslotsFull") }}
                </VAlert>
            </template>
            <template v-else>
                <checkout-custom-fields
                    :fields="
                        orderStore.options.customFields.filter(
                            (field) => field.placement === 'pre_products',
                        )
                    "
                />
                <checkout-order-selection />
                <checkout-custom-fields
                    :fields="
                        orderStore.options.customFields.filter(
                            (field) => field.placement === 'post_products',
                        )
                    "
                />
                <checkout-timeslot />
                <checkout-donation />
                <checkout-customer-remark />
                <checkout-payment-method />
                <checkout-legal-fields />
                <span class="pb-4">
                    <VueHcaptcha
                        ref="hcaptchaComponent"
                        :sitekey="
                            orderStore.options.configuration.hcaptchaSiteKey
                        "
                        language="nl-nl"
                        @verify="onHcaptchaVerify"
                    />
                </span>

                <div
                    class="text-medium-emphasis font-weight-bold"
                    data-cy="total-price"
                >
                    Totaalprijs:
                    {{ $n(orderStore.meta.totalPrice / 100, "currency") }}
                </div>

                <VAlert v-if="submitError" type="error" class="my-4">
                    {{ submitError }}
                </VAlert>

                <VBtn
                    :disabled="!orderStore.validation.isValid"
                    :loading="sendingOrder"
                    color="primary"
                    @click="submit"
                >
                    {{ $t("checkout.submitLabel") }}
                </VBtn>
            </template>
        </template>
    </VForm>
</template>
